// R and Python access snippets for the "Use this series" card. Pure, so the
// quoting rules can be unit-tested (main.ts boots the whole app on import).
//
// ds() takes the dimension filters as named arguments, which only works when a
// dimension name is a valid identifier. A name such as "Offene Stellen" is not:
// R needs backticks, and Python cannot take it as a keyword argument at all, so
// the filters go in the `dims` dict (ds(id, {"Offene Stellen": "1"})).

export interface SnippetSpec { id: string; dims: Record<string, string[]> }

const R_RESERVED = new Set(["if", "else", "repeat", "while", "function", "for", "next", "break",
  "TRUE", "FALSE", "NULL", "Inf", "NaN", "NA", "NA_integer_", "NA_real_", "NA_character_", "in"]);
// Names ds() itself uses for its own arguments, so a dimension of that name
// would be swallowed by the argument. Python only: R's ds() takes dims via `...`.
const PY_RESERVED = new Set(["dataset", "dims", "start", "end", "wide",
  "False", "None", "True", "and", "as", "assert", "async", "await", "break", "class", "continue",
  "def", "del", "elif", "else", "except", "finally", "for", "from", "global", "if", "import", "in",
  "is", "lambda", "nonlocal", "not", "or", "pass", "raise", "return", "try", "while", "with", "yield"]);

const q = (s: string): string => JSON.stringify(s);   // "..." with " and \ escaped

/** A dimension name as an R argument name: bare when valid, else in backticks. */
export function rName(name: string): string {
  const ok = /^(?:[A-Za-z]|\.(?![0-9]))[A-Za-z0-9._]*$/.test(name) && !R_RESERVED.has(name);
  return ok ? name : "`" + name.replace(/\\/g, "\\\\").replace(/`/g, "\\`") + "`";
}

const pyIdent = (name: string): boolean => /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !PY_RESERVED.has(name);

const rVal = (codes: string[]): string =>
  codes.length === 1 ? q(codes[0] as string) : `c(${codes.map(q).join(", ")})`;
const pyVal = (codes: string[]): string =>
  codes.length === 1 ? q(codes[0] as string) : `[${codes.map(q).join(", ")}]`;

/** `ds("id", dim = "code", ...)` in R. */
export function rCall(s: SnippetSpec): string {
  const args = Object.entries(s.dims).map(([d, codes]) => `${rName(d)} = ${rVal(codes)}`);
  return `ds(${[q(s.id), ...args].join(", ")})`;
}

/** `ds("id", dim="code", ...)` in Python; when any dimension name is not a usable
 *  keyword, all filters go in one dims dict: `ds("id", {"Offene Stellen": "1"})`. */
export function pythonCall(s: SnippetSpec): string {
  const entries = Object.entries(s.dims);
  if (entries.every(([d]) => pyIdent(d)))
    return `ds(${[q(s.id), ...entries.map(([d, codes]) => `${d}=${pyVal(codes)}`)].join(", ")})`;
  return `ds(${q(s.id)}, {${entries.map(([d, codes]) => `${q(d)}: ${pyVal(codes)}`).join(", ")}})`;
}

/** The whole R snippet: one data frame per series, numbered when there are several. */
export function rSnippet(specs: SnippetSpec[]): string {
  if (specs.length === 1) return `library(dataseries)\ndf <- ${rCall(specs[0] as SnippetSpec)}`;
  return `library(dataseries)\n` + specs.map((s, i) => `df${i + 1} <- ${rCall(s)}`).join("\n\n");
}

/** The whole Python snippet. */
export function pythonSnippet(specs: SnippetSpec[]): string {
  if (specs.length === 1) return `from dataseries import ds\ndf = ${pythonCall(specs[0] as SnippetSpec)}`;
  return `from dataseries import ds\n` + specs.map((s, i) => `df${i + 1} = ${pythonCall(s)}`).join("\n\n");
}
